import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import axios from "axios";
import { calendar } from "@/components/ParishCalendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Standing weekly Mass schedule (fallback when no specific calendar entry exists)
const weeklyFallback = {
  0: [
    // Sunday
    { t: "6:00 AM", e: "Holy Mass", v: "Parish Church · Cebuano" },
    { t: "8:00 AM", e: "Holy Mass", v: "Parish Church · English" },
    { t: "9:30 AM", e: "Baptism", v: "Parish Church" },
    { t: "3:00 PM", e: "Holy Mass", v: "Parish Church · Ilonggo" },
    { t: "5:00 PM", e: "Holy Mass", v: "Parish Church · English" },
  ],
  1: [{ t: "6:00 AM", e: "Holy Mass", v: "Parish Church" }], // Monday
  2: [{ t: "—", e: "No Mass Schedule", v: "" }], // Tuesday
  3: [
    { t: "6:00 AM", e: "Holy Mass", v: "Parish Church" },
    { t: "5:30 PM", e: "Holy Mass", v: "Parish Church" },
  ], // Wednesday
  4: [
    { t: "6:00 AM", e: "Holy Mass", v: "Parish Church" },
    { t: "2:00 PM", e: "Interview", v: "Parish Office" },
  ], // Thursday
  5: [
    { t: "6:00 AM", e: "Holy Mass", v: "Parish Church" },
    { t: "7:00 AM", e: "Confession", v: "Parish Church" },
  ], // Friday
  6: [
    { t: "6:00 AM", e: "Holy Mass", v: "Parish Church" },
    { t: "5:00 PM", e: "Holy Mass", v: "Parish Church" },
  ], // Saturday
};

// Get current date in Manila TZ (Y, M, D, weekday)
function manilaNow() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    y: Number(get("year")),
    m: Number(get("month")),
    d: Number(get("day")),
    weekday: dayMap[get("weekday")],
  };
}

const MONTHS_SHORT = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function todaySchedule({ y, m, d, weekday }) {
  // Try matching the static parish calendar by Y-M-D
  const match = calendar.find((entry) => {
    const dt = new Date(entry.date);
    return (
      !Number.isNaN(dt.getTime()) &&
      dt.getFullYear() === y &&
      dt.getMonth() + 1 === m &&
      dt.getDate() === d
    );
  });
  if (match) return match.events || [];
  return weeklyFallback[weekday] || [];
}

export default function FeastBanner() {
  const [open, setOpen] = useState(true);
  const [saint, setSaint] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const today = useMemo(manilaNow, []);
  const events = useMemo(() => todaySchedule(today), [today]);
  const dayShort = MONTHS_SHORT[today.m - 1];
  const dateLabel = `${MONTHS_LONG[today.m - 1]} ${today.d}, ${today.y}`;

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/saint-today`, { timeout: 15000 })
      .then((r) => {
        if (!alive) return;
        if (r.data && !r.data.unavailable) setSaint(r.data);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  if (!open) return null;
  // Hide entirely if scraper unavailable AND we have nothing else useful — but we still have the date + Mass times, so keep it.

  const saintName = saint?.name || "Saint of the Day";
  const sourceUrl = saint?.source;
  const bio = saint?.description || "";
  const rank = saint?.rank || "";
  const prayer = saint?.prayer || "";

  // Map liturgical colour to a small swatch (purely decorative)
  const colourSwatch =
    {
      white: "bg-parchment-100 border border-cocoa-900/20",
      red: "bg-terracotta",
      green: "bg-sage-dark",
      violet: "bg-purple-800",
      rose: "bg-pink-300",
      gold: "bg-gold",
    }[saint?.colour] || null;

  return (
    <aside
      data-testid="feast-banner"
      className="relative bg-cocoa-900 text-parchment-100 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-cocoa-900 via-cocoa-800 to-cocoa-900" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_30%,rgba(212,175,55,0.22),transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-5 md:py-6">
        <div className="grid md:grid-cols-12 gap-5 items-center">
          {/* Date chip + Saint of the Day */}
          <div className="md:col-span-6 flex items-start gap-4">
            <div className="hidden md:flex flex-col items-center bg-parchment-100 text-cocoa-900 w-16 py-2 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-[0.22em] text-terracotta">
                {dayShort}
              </span>
              <span className="font-serif text-3xl leading-none mt-0.5">{today.d}</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-cocoa-700 mt-0.5">
                {today.y}
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold font-sans mb-1">
                <span>✦</span>
                <span>Saint of the Day</span>
                {rank && (
                  <span
                    data-testid="feast-banner-rank"
                    className="inline-flex items-center gap-1.5 ml-1 px-2 py-0.5 border border-gold/40 text-gold/90 tracking-[0.22em] text-[9px]"
                  >
                    {colourSwatch && (
                      <span className={`inline-block w-1.5 h-1.5 ${colourSwatch}`} aria-hidden />
                    )}
                    {rank}
                  </span>
                )}
              </div>
              <h2
                data-testid="feast-banner-saint"
                className="font-serif text-xl md:text-2xl lg:text-[1.6rem] text-parchment-100 leading-tight"
              >
                {sourceUrl ? (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors"
                  >
                    <span className={loaded ? "" : "opacity-60"}>{saintName}</span>
                  </a>
                ) : (
                  <span className={loaded ? "" : "opacity-60"}>{saintName}</span>
                )}
              </h2>
              {(bio || prayer) && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="feast-banner-readmore"
                      className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.28em] text-gold hover:text-parchment-100 transition-colors font-sans"
                    >
                      Read more
                      <span aria-hidden>→</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    data-testid="feast-banner-bio"
                    className="w-[320px] md:w-[380px] bg-parchment-50 border border-cocoa-900/15 text-cocoa-900 shadow-xl rounded-none p-0"
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-terracotta font-sans">
                          ✦ {dateLabel}
                        </div>
                        {rank && (
                          <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-cocoa-700">
                            {colourSwatch && (
                              <span className={`inline-block w-1.5 h-1.5 ${colourSwatch}`} aria-hidden />
                            )}
                            {rank}
                          </span>
                        )}
                      </div>
                      <h3 className="font-serif text-lg text-cocoa-900 leading-snug mb-3">
                        {saintName}
                      </h3>
                      <p className="font-sans text-sm text-cocoa-800/90 leading-relaxed">
                        {bio || (
                          <span className="italic text-cocoa-700">
                            A blessed day in the Lord. Let us walk in faith and serve one another in love.
                          </span>
                        )}
                      </p>

                      {prayer && (
                        <div className="mt-5 pt-4 border-t border-cocoa-900/10">
                          <div className="text-[9px] uppercase tracking-[0.3em] text-gold font-sans mb-2">
                            ✦ Let us pray
                          </div>
                          <p
                            data-testid="feast-banner-prayer"
                            className="font-serif italic text-[15px] text-cocoa-900 leading-relaxed"
                          >
                            {prayer}
                          </p>
                        </div>
                      )}

                      {sourceUrl && (
                        <a
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-block text-[10px] uppercase tracking-[0.28em] text-terracotta hover:text-cocoa-900 font-sans"
                        >
                          Vatican News ↗
                        </a>
                      )}
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
                  </PopoverContent>
                </Popover>
              )}
              <div className="md:hidden mt-1 text-[10px] uppercase tracking-[0.22em] text-parchment-200/70">
                {dateLabel}
              </div>
            </div>
          </div>

          {/* Today's Mass schedule */}
          <div className="md:col-span-5 md:border-l md:border-parchment-100/15 md:pl-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2 font-sans">
              Today&rsquo;s Holy Eucharist
            </div>
            <div
              data-testid="feast-banner-masses"
              className="flex flex-col gap-y-1.5 font-sans text-parchment-100"
            >
              {events.length === 0 ? (
                <div className="text-[11px] uppercase tracking-[0.22em] text-parchment-200/70 italic">
                  Please check with the parish office.
                </div>
              ) : (
                events.slice(0, 4).map((ev, i) => (
                  <div
                    key={i}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
                  >
                    <span className="font-serif text-lg text-parchment-100 leading-none">
                      {ev.t}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-parchment-200/70">
                      {ev.e}
                    </span>
                    {ev.v && (
                      <span className="text-[11px] italic text-parchment-200/85 leading-snug">
                        · {ev.v}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Close */}
          <div className="md:col-span-1 flex justify-end">
            <button
              onClick={() => setOpen(false)}
              aria-label="Dismiss announcement"
              data-testid="feast-banner-close"
              className="text-parchment-200/70 hover:text-gold transition-colors p-1"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom gold rule */}
      <div className="h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
    </aside>
  );
}
